/**
 * Template mock injection + connection remapping — pure / DI helpers.
 *
 * `@/utils/templates` stays within the `utils/* → @/types/* | @/utils/*`
 * layering rule, so the store reads/writes these helpers used to perform are
 * lifted out to the callers:
 *
 *   - `computeTemplateMockedNodes` builds the next `mockedNodes` snapshot from
 *     the current workflow + template extras, preserving in-flight user edits.
 *     Pure — the caller passes the workflow / existing mocks / template data in
 *     and writes the result back via `workflowStore.setMockedNodes`.
 *   - `remapWorkflowReference` rewrites a single workflow reference
 *     (`oldRefKey` → `newRefKey`) in the definition and rebinds the affected
 *     nodes. Store access is injected through a `WorkflowReferenceRemapSurface`
 *     so this module imports no stores.
 */

import type { WorkflowDefinition } from '@/types/workflowDefinition';
import type { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';
import { patchWorkflowConnections } from './connectionResolver';
import { walkAllActions } from '@/utils/workflow';

/** The workflow store's `mockedNodes` map: node id → mock / trigger payload. */
export type MockedNodes = Record<string, MockNodeInfo | TriggerMockPayload>;

/**
 * Recursively walk the action tree (incl. Scope / If.else / Switch.cases /
 * Switch.default branches, and Agent tools) and populate `next` with the
 * merged mock state.
 *
 * Priority per node:
 *   1. Preserve `existing[nodeId]` so user edits survive re-injection.
 *   2. Seed from a matching `templateMocks` entry so the template's mock data
 *      appears on load (with `enabled: true` when the entry ships canned
 *      output).
 *   3. Otherwise skip — no mock surface for that node.
 *
 * `walkAllActions` supplies the candidate `names` (for regular actions
 * `[name]`; for agent tools `[toolName, innerActionName]` so a template can
 * key by either), the canvas-scoped `scopedName` (written as `actionName`),
 * and the ready-to-use `nodeId` (`action-…` for regular actions, `tool-…` for
 * agent tools).
 */
function collectActionCandidates(
  actions: Record<string, unknown> | undefined,
  existing: MockedNodes,
  templateMocks: Record<string, MockNodeInfo>,
  next: MockedNodes,
): void {
  walkAllActions(actions, (names, _action, scopedName, nodeId) => {
    const mockKey = names.find((name) => templateMocks[name]) ?? null;
    if (existing[nodeId]) {
      next[nodeId] = existing[nodeId]!;
    } else if (mockKey) {
      const seed = templateMocks[mockKey]!;
      next[nodeId] = {
        ...seed,
        actionName: scopedName,
        // Mocks that ship canned output default to ON.
        ...(seed.mockOutput ? { enabled: true } : {}),
      };
    }
  });
}

/**
 * Build the next `mockedNodes` snapshot for an applied template.
 *
 * Pure — does not touch any store. Caller passes in the current workflow,
 * existing mocks, and the template's mock data, then writes the result back
 * via `workflowStore.setMockedNodes`.
 *
 * Idempotent — entries for actions/triggers that no longer exist in the
 * workflow are dropped.
 */
export function computeTemplateMockedNodes(
  workflow: WorkflowDefinition | null | undefined,
  existing: MockedNodes,
  templateMocks: Record<string, MockNodeInfo>,
  triggerSample: TriggerMockPayload | undefined,
): MockedNodes {
  const next: MockedNodes = {};

  if (workflow?.definition?.triggers) {
    for (const name of Object.keys(workflow.definition.triggers)) {
      const nodeId = `trigger-${name}`;
      if (existing[nodeId]) {
        next[nodeId] = existing[nodeId]!;
      } else if (triggerSample) {
        next[nodeId] = triggerSample;
      }
    }
  }

  collectActionCandidates(
    workflow?.definition?.actions as Record<string, unknown> | undefined,
    existing,
    templateMocks,
    next,
  );

  return next;
}

/**
 * Store surface the per-reference remap needs. Injected so this module stays
 * free of store imports (keeps `utils/templates/` within the layering rule).
 * Callers wire it to the workflow + connection stores.
 */
export interface WorkflowReferenceRemapSurface {
  /** The current workflow definition, or null/undefined when none is loaded. */
  readonly workflow: WorkflowDefinition | null | undefined;
  /** True when `key` exists in the connection store's references. */
  hasReference: (key: string) => boolean;
  /** Rebind every node currently mapped to `oldKey` onto `newKey`. */
  remapNodesToReference: (oldKey: string, newKey: string) => void;
  /** Persist the patched workflow definition. */
  updateWorkflow: (workflow: WorkflowDefinition) => void;
}

/**
 * Immediately remap a single workflow reference to a different one: patch
 * `oldRefKey` → `newRefKey` in the workflow definition and rebind every node
 * whose mapping currently points at `oldRefKey`.
 *
 * Does NOT delete any reference — both keys stay in connections.json so the
 * user can still pick the old reference again, and other workflows that share
 * the same `connections.json` aren't accidentally pruned. Per-reference (no
 * apiId-wide cascade): only the affected nodes' bindings move.
 *
 * No-op when `oldRefKey === newRefKey`, either key is empty, the new reference
 * is missing, or there's no workflow loaded.
 *
 * All store access is injected via `surface` so this helper imports no stores;
 * the caller (the Connections dialog) supplies the workflow/connection stores.
 */
export function remapWorkflowReference(
  oldRefKey: string,
  newRefKey: string,
  surface: WorkflowReferenceRemapSurface,
): void {
  if (!oldRefKey || !newRefKey || oldRefKey === newRefKey || !surface.workflow) return;
  if (!surface.hasReference(newRefKey)) return;

  const connectionMap = new Map<string, string>([[oldRefKey, newRefKey]]);
  const patchedWorkflow = patchWorkflowConnections(surface.workflow, connectionMap);

  surface.remapNodesToReference(oldRefKey, newRefKey);
  surface.updateWorkflow(patchedWorkflow);
}
